import type { GrassCharacteristics } from '@/lib/types/recommendation-engine'

export const CARPETGRASS_SOURCES = [
  'https://www.lsuagcenter.com/profiles/aiverson/articles/page1551975010823',
  'https://www.lsuagcenter.com/topics/lawn_garden/home_gardening/lawn/turfgrass_varieties/carpetgrass-for-lawns',
] as const

export const CARPETGRASS_IDENTIFY_CARE_TIPS = [
  'Check soil moisture and visible stress before watering common carpetgrass. No verified weekly amount is available.',
  'Mow common carpetgrass at 1-2 inches and avoid cutting more than one-third of the blade at once.',
  'Use a soil test before adding fertilizer. Common carpetgrass needs little nitrogen, and none in fall or winter.',
] as const

// Common carpetgrass only. Axonopus compressus needs a separate care profile.
// LSU AgCenter sources above checked 2026-09-23. Irrigation depth and weekly
// water amounts are omitted because these sources do not establish them.
export const CARPETGRASS: GrassCharacteristics = {
  name: 'Carpetgrass',
  season: 'Warm',
  bladeWidth: 'Coarse; between centipedegrass and St. Augustinegrass',
  texture: 'Coarse',
  growthHabit: 'Spreading by surface runners (stolons)',
  bladeShape: 'Flat blades with rounded tips',
  color: 'Light to medium green',
  density: 'Low-growing, spreading turf',
  rootSystem: 'Fibrous roots; stolons root at nodes',
  photoIdCues: [
    'Common carpetgrass is Axonopus fissifolius (synonym Axonopus affinis); do not merge broadleaf carpetgrass, Axonopus compressus, into this label',
    'Close-up of a runner: alternate leaves along the stolon, unlike the opposite arrangement on St. Augustinegrass',
    'When present, seedheads usually have two upper spikes in a V and often a third spike below; centipedegrass and St. Augustinegrass have a single spike',
    'If runners, leaf arrangement, and seedheads are hidden and lookalikes remain plausible, request a runner or seedhead close-up instead of forcing Carpetgrass',
  ],
  seedhead: 'Usually two upper spikes in a V, often with a third spike below',
  dormancyAppearance: 'Browns with cold weather or frost',
  lookalikes: [
    { grass: 'Centipede', tell: 'Centipedegrass has a single seed spike; common carpetgrass has multiple spikes when visible' },
    { grass: 'St. Augustinegrass', tell: 'St. Augustinegrass has broader blades and opposite leaves on runners; common carpetgrass has alternate leaves' },
    { grass: 'Bahiagrass', tell: 'Both can have V-shaped seedheads. A seedhead alone does not settle the identification; inspect the blades and runners' },
  ],
  droughtTolerance: 'Low',
  shadeTolerance: 'Medium',
  trafficTolerance: 'Low',
  diseaseResistance: 'Medium',
  mowingHeight: '1-2 inches',
  mowingDetails: {
    optimal: '1-2 inches', minimum: '1 inch', maximum: '2 inches',
    frequency: { growingSeason: 'As needed to remove no more than one-third of the blade', peakGrowth: 'Mow often enough to control seedheads', dormantSeason: 'Only if actively growing' },
    tips: ['Use sharp mower blades', 'Remove no more than one-third of the blade at a time'],
  },
  soilPreference: 'Moist, acidic soil; avoid standing water',
  pHRange: '4.0-7.0',
  maintenanceLevel: 'Low',
  coldTolerance: 'Low; suited to mild southern climates',
  establishmentTime: 'Seed or plugs during active warm-season growth; follow the seed label',
  peakGrowth: 'Late spring and summer',
  dormant: 'Cold weather and frost',
  fertilization: 'Little fertilizer needed. LSU AgCenter permits up to 1 lb nitrogen per 1,000 sq ft per year during active growth',
  fertilizationNeeds: {
    annualNPK: { nitrogen: 1, phosphorus: 0, potassium: 0 },
    seasonalSplit: { spring: 50, summer: 50, fall: 0, winter: 0 },
    timing: { firstApplication: 'After spring green-up, only if needed', lastApplication: 'Midsummer; keep the annual total at or below 1 lb N per 1,000 sq ft', frequency: 'If needed, no more than 0.5 lb N per 1,000 sq ft per application; phosphorus and potassium only from a soil test' },
  },
  overseeding: 'Do not routinely winter-overseed. Repair thin areas with common carpetgrass seed or plugs during active growth',
}
